import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { REVIEW_GRADES, type PublicQuestion, type QuestionFeedback } from "@book2class/shared/learning";
import { api, ApiError } from "../../api";
import { Markdown } from "../../components/markdown";
import { Spinner } from "../../components/ui";
import { AssistantPanel, LearnCrumb, QuizRunner, VisualSummary, type Visual } from "./components";
import { MyReports, ReportButton } from "../../components/report";

function useCourseTitle(courseId: string) {
  const [title, setTitle] = useState("");
  useEffect(() => {
    api
      .get<{ course: { title: string } }>(`/learn/${courseId}`)
      .then((t) => setTitle(t.course.title))
      .catch(() => undefined);
  }, [courseId]);
  return title || "Curso";
}

// ─────────────────────────── Flashcards (SM-2) ───────────────────────────

type Card = { id: string; front: string; back: string; module: string; isNew: boolean };

export function FlashcardsPage() {
  const { courseId = "" } = useParams();
  const [params] = useSearchParams();
  const moduleId = params.get("modulo");
  const title = useCourseTitle(courseId);
  const [cards, setCards] = useState<Card[] | null>(null);
  const [stats, setStats] = useState<{ due: number; total: number; learned: number } | null>(null);
  const [flipped, setFlipped] = useState(false);
  const [reviewed, setReviewed] = useState(0);

  const load = useCallback(() => {
    api
      .get<{ cards: Card[]; stats: { due: number; total: number; learned: number } }>(
        `/learn/${courseId}/flashcards${moduleId ? `?module=${moduleId}` : ""}`,
      )
      .then((r) => {
        setCards(r.cards);
        setStats(r.stats);
      })
      .catch(() => setCards([]));
  }, [courseId, moduleId]);
  useEffect(load, [load]);

  const card = cards?.[0];
  async function grade(g: number) {
    if (!card) return;
    const r = await api.post<{ intervalDays: number }>(`/learn/${courseId}/flashcards/${card.id}/review`, {
      grade: g,
    });
    setReviewed((n) => n + 1);
    setFlipped(false);
    // Cartões marcados como "Errei" voltam ao fim da fila da sessão.
    setCards((cs) =>
      cs ? [...cs.slice(1), ...(r.intervalDays === 0 ? [{ ...card, isNew: false }] : [])] : cs,
    );
  }

  return (
    <main className="page narrow">
      <LearnCrumb courseId={courseId} title={title} here="Revisão" />
      <h1>Flashcards</h1>
      <p className="segment-meta">
        Repetição espaçada: cada cartão volta no momento certo para fixar a memória.
      </p>
      {!cards ? (
        <Spinner />
      ) : !card ? (
        <div className="card empty">
          <h2>{reviewed ? "Revisão concluída!" : "Nada para revisar agora"}</h2>
          <p>
            {reviewed ? `Você revisou ${reviewed} cartão(ões). ` : ""}
            {stats ? `${stats.learned} de ${stats.total} cartões memorizados.` : ""} Os próximos aparecem
            quando for a hora de revisar.
          </p>
          <Link className="btn" to={`/aluno/cursos/${courseId}`}>
            Voltar à trilha
          </Link>
        </div>
      ) : (
        <>
          <div className="segment-meta" style={{ marginBottom: 8 }}>
            {cards.length} na fila · {reviewed} revisado(s) nesta sessão
          </div>
          <button
            type="button"
            className={`flashcard${flipped ? " flipped" : ""}`}
            onClick={() => setFlipped(!flipped)}
            aria-label={flipped ? "Ver frente" : "Ver resposta"}
          >
            <span className="fc-meta">
              {card.module}
              {card.isNew ? " · novo" : ""}
            </span>
            <span className="fc-text">{flipped ? card.back : card.front}</span>
            <span className="fc-hint">{flipped ? "Como foi lembrar?" : "Toque para ver a resposta"}</span>
          </button>
          <div className="fc-report">
            <ReportButton
              courseId={courseId}
              itemType="flashcard"
              itemId={card.id}
              itemLabel={card.front}
              compact
            />
          </div>
          {flipped && (
            <div className="grades">
              {REVIEW_GRADES.map((g) => (
                <button
                  key={g.grade}
                  type="button"
                  className={`btn ${g.grade < 3 ? "ghost" : ""}`}
                  onClick={() => void grade(g.grade)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      <AssistantPanel courseId={courseId} />
    </main>
  );
}

// ─────────────────────────── Estudo de caso e exercícios ───────────────────────────

type CaseStudy = {
  id: string;
  title: string;
  scenario: string;
  startNodeId: string;
  nodes: Array<{
    id: string;
    text: string;
    choices: Array<{ label: string; nextNodeId: string; feedback: string; isBest: boolean }>;
  }>;
};
type Practice = {
  module: {
    id: string;
    position: number;
    title: string;
    summary: string | null;
    visualSummary: Visual | null;
  };
  caseStudies: CaseStudy[];
  exercises: Array<{ id: string; title: string; statement: string; answerKey: string }>;
};

function CasePlayer({ cs, courseId }: { cs: CaseStudy; courseId: string }) {
  const [path, setPath] = useState<Array<{ node: string; choice?: number }>>([{ node: cs.startNodeId }]);
  const current = path.at(-1)!;
  const node = cs.nodes.find((n) => n.id === current.node);
  return (
    <section className="card card-pad case">
      <div className="item-head">
        <div className="eyebrow">Estudo de caso</div>
        <ReportButton courseId={courseId} itemType="case_study" itemId={cs.id} itemLabel={cs.title} compact />
      </div>
      <h2 style={{ fontSize: "1.2rem" }}>{cs.title}</h2>
      <Markdown text={cs.scenario} />
      {path.slice(0, -1).map((p, i) => {
        const n = cs.nodes.find((x) => x.id === p.node)!;
        const ch = n.choices[p.choice!]!;
        return (
          <div key={i} className="case-step past">
            <p>{n.text}</p>
            <p className={`case-choice ${ch.isBest ? "best" : "other"}`}>
              <strong>Sua escolha:</strong> {ch.label}
              <br />
              <span>{ch.feedback}</span>
            </p>
          </div>
        );
      })}
      {node && (
        <div className="case-step">
          <p>{node.text}</p>
          {node.choices.length ? (
            <div className="case-options">
              {node.choices.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  className="btn ghost"
                  onClick={() =>
                    setPath([...path.slice(0, -1), { node: current.node, choice: i }, { node: c.nextNodeId }])
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="actions" style={{ justifyContent: "flex-start" }}>
              <span className="badge ok">Fim do caso</span>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => setPath([{ node: cs.startNodeId }])}
              >
                Recomeçar e testar outro caminho
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function PracticePage() {
  const { courseId = "", moduleId = "" } = useParams();
  const title = useCourseTitle(courseId);
  const [data, setData] = useState<Practice | null>(null);
  const [error, setError] = useState("");
  const [shown, setShown] = useState<Record<string, boolean>>({});
  useEffect(() => {
    api
      .get<Practice>(`/learn/${courseId}/modules/${moduleId}/practice`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar."));
  }, [courseId, moduleId]);
  if (error)
    return (
      <main className="page">
        <p className="alert error">{error}</p>
      </main>
    );
  if (!data)
    return (
      <main className="page">
        <Spinner />
      </main>
    );
  return (
    <main className="page narrow">
      <LearnCrumb courseId={courseId} title={title} here={`Módulo ${data.module.position}`} />
      <h1>Prática: {data.module.title}</h1>
      <VisualSummary visual={data.module.visualSummary} />
      {data.caseStudies.map((c) => (
        <CasePlayer key={c.id} cs={c} courseId={courseId} />
      ))}
      {data.exercises.map((e) => (
        <section key={e.id} className="card card-pad exercise">
          <div className="item-head">
            <div className="eyebrow">Exercício prático</div>
            <ReportButton courseId={courseId} itemType="exercise" itemId={e.id} itemLabel={e.title} compact />
          </div>
          <h2 style={{ fontSize: "1.15rem" }}>{e.title}</h2>
          <Markdown text={e.statement} />
          <p className="segment-meta">Resolva por escrito antes de conferir o gabarito.</p>
          {shown[e.id] ? (
            <div className="answer-key">
              <div className="eyebrow">Gabarito comentado</div>
              <Markdown text={e.answerKey} />
            </div>
          ) : (
            <button
              type="button"
              className="btn ghost small"
              onClick={() => setShown({ ...shown, [e.id]: true })}
            >
              Ver gabarito
            </button>
          )}
        </section>
      ))}
      <Link className="btn ghost" to={`/aluno/cursos/${courseId}`}>
        Voltar à trilha
      </Link>
      <AssistantPanel courseId={courseId} />
    </main>
  );
}

// ─────────────────────────── Avaliação final ───────────────────────────

export function FinalExamPage() {
  const { courseId = "" } = useParams();
  const title = useCourseTitle(courseId);
  const [exam, setExam] = useState<{
    attemptId: string;
    passingScore: number;
    questions: PublicQuestion[];
  } | null>(null);
  const [result, setResult] = useState<{
    passed: boolean;
    score: number;
    certificate: { code: string } | null;
  } | null>(null);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);

  async function start() {
    setError("");
    setResult(null);
    try {
      setExam(await api.post(`/learn/${courseId}/final`));
      setStarted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não foi possível iniciar a avaliação.");
    }
  }

  return (
    <main className="page narrow">
      <LearnCrumb courseId={courseId} title={title} here="Avaliação final" />
      <h1>Avaliação final</h1>
      {error && <p className="alert error">{error}</p>}
      {!started ? (
        <div className="card card-pad">
          <p>
            As questões são sorteadas do banco do curso a cada tentativa. Responda com calma: a correção
            acontece ao enviar.
          </p>
          <button className="btn" onClick={() => void start()}>
            Iniciar avaliação
          </button>
        </div>
      ) : !exam ? (
        <Spinner />
      ) : (
        <>
          <p className="segment-meta">
            {exam.questions.length} questões{exam.passingScore ? ` · nota mínima ${exam.passingScore}%` : ""}
          </p>
          <div className="card card-pad">
            <QuizRunner
              key={exam.attemptId}
              reportCourseId={courseId}
              questions={exam.questions}
              allowRetry={false}
              submitLabel="Enviar avaliação"
              submit={async (answers) => {
                const r = await api.post<{
                  score: number;
                  passed: boolean;
                  feedback: QuestionFeedback[];
                  certificate: { code: string } | null;
                }>(`/learn/${courseId}/final/${exam.attemptId}`, { answers });
                setResult(r);
                return r;
              }}
            />
          </div>
          {result && (
            <div className={`card card-pad final-result ${result.passed ? "ok" : "ko"}`}>
              <h2>{result.passed ? "Aprovado(a)! 🎉" : "Ainda não foi desta vez"}</h2>
              <p>
                Sua nota: <strong>{result.score}%</strong>
                {exam.passingScore ? ` (mínimo ${exam.passingScore}%)` : ""}.
              </p>
              <div className="actions" style={{ justifyContent: "flex-start" }}>
                {result.certificate ? (
                  <>
                    <a className="btn" href={`/api/learn/${courseId}/certificate.pdf`}>
                      Baixar certificado
                    </a>
                    <Link className="btn ghost" to={`/certificado/${result.certificate.code}`}>
                      Página de validação
                    </Link>
                  </>
                ) : (
                  <button className="btn" onClick={() => void start()}>
                    Nova tentativa
                  </button>
                )}
                <Link className="btn ghost" to={`/aluno/cursos/${courseId}`}>
                  Voltar à trilha
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}

// ─────────────────────────── Glossário, busca e anotações ───────────────────────────

type Hit = {
  id: string;
  kind: string;
  title: string;
  context?: string;
  lessonId?: string;
  snippet: string;
  locked: boolean;
};

export function StudyToolsPage() {
  const { courseId = "" } = useParams();
  const title = useCourseTitle(courseId);
  const [terms, setTerms] = useState<Array<{ id: string; term: string; definition: string }>>([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [notebook, setNotebook] = useState<{
    notes: Array<{ id: string; body: string; lessonId: string; lessonTitle: string }>;
    bookmarks: Array<{ lessonId: string; lessonTitle: string }>;
  } | null>(null);
  useEffect(() => {
    api
      .get<typeof terms>(`/learn/${courseId}/glossary`)
      .then(setTerms)
      .catch(() => undefined);
    api
      .get<NonNullable<typeof notebook>>(`/learn/${courseId}/notebook`)
      .then(setNotebook)
      .catch(() => undefined);
  }, [courseId]);
  const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const shown = terms.filter((t) => !filter || norm(t.term + " " + t.definition).includes(norm(filter)));

  return (
    <main className="page">
      <LearnCrumb courseId={courseId} title={title} here="Consulta" />
      <h1>Glossário, busca e anotações</h1>
      <form
        className="card card-pad search-box"
        role="search"
        onSubmit={async (e) => {
          e.preventDefault();
          const r = await api
            .get<{ results: Hit[] }>(`/learn/${courseId}/search?q=${encodeURIComponent(q)}`)
            .catch(() => ({ results: [] }));
          setHits(r.results);
        }}
      >
        <label htmlFor="s" className="label">
          Buscar em todo o curso
        </label>
        <div className="search-row">
          <input
            id="s"
            type="text"
            placeholder="Ex.: margem de contribuição"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="btn" disabled={q.trim().length < 2}>
            Buscar
          </button>
        </div>
        {hits && (
          <ul className="search-results">
            {!hits.length && <li className="segment-meta">Nada encontrado.</li>}
            {hits.map((h) => (
              <li key={h.id}>
                {h.lessonId && !h.locked ? (
                  <Link to={`/aluno/cursos/${courseId}/aulas/${h.lessonId}`}>{h.title}</Link>
                ) : (
                  <strong>{h.title}</strong>
                )}
                <span className="segment-meta">
                  {" "}
                  · {h.kind === "glossary" ? "Glossário" : h.context}
                  {h.locked ? " · aula ainda bloqueada" : ""}
                </span>
                <div className="snippet">{h.snippet}</div>
              </li>
            ))}
          </ul>
        )}
      </form>
      <div className="two-col">
        <section className="card card-pad">
          <div className="item-head">
            <h2 style={{ fontSize: "1.1rem", margin: 0 }}>Glossário</h2>
            <input
              type="text"
              aria-label="Filtrar termos"
              placeholder="Filtrar termos"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ maxWidth: 220 }}
            />
          </div>
          <dl className="glossary">
            {shown.map((t) => (
              <div key={t.id}>
                <dt>
                  {t.term}{" "}
                  <ReportButton
                    courseId={courseId}
                    itemType="glossary"
                    itemId={t.id}
                    itemLabel={t.term}
                    compact
                  />
                </dt>
                <dd>{t.definition}</dd>
              </div>
            ))}
          </dl>
          {!shown.length && <p className="segment-meta">Nenhum termo encontrado.</p>}
        </section>
        <section className="card card-pad side-col">
          <h2 style={{ fontSize: "1.05rem" }}>Aulas marcadas</h2>
          {notebook?.bookmarks.length ? (
            <ul className="plain-list">
              {notebook.bookmarks.map((b) => (
                <li key={b.lessonId}>
                  <Link to={`/aluno/cursos/${courseId}/aulas/${b.lessonId}`}>★ {b.lessonTitle}</Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="segment-meta">Use “Marcar aula” para guardar aulas aqui.</p>
          )}
          <h2 style={{ fontSize: "1.05rem" }}>Anotações</h2>
          {notebook?.notes.length ? (
            <ul className="note-list">
              {notebook.notes.map((n) => (
                <li key={n.id}>
                  <p>{n.body}</p>
                  <Link className="segment-meta" to={`/aluno/cursos/${courseId}/aulas/${n.lessonId}`}>
                    {n.lessonTitle}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="segment-meta">Suas anotações das aulas aparecem aqui.</p>
          )}
        </section>
      </div>
      <div style={{ marginTop: 16 }}>
        <MyReports courseId={courseId} />
      </div>
      <AssistantPanel courseId={courseId} />
    </main>
  );
}

// ─────────────────────────── Validação pública do certificado ───────────────────────────

/** Campo para digitar o código do certificado e abrir a validação. */
function CertificateCodeForm({ initial = "" }: { initial?: string }) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initial);
  return (
    <form
      className="form"
      style={{ marginTop: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        const code = value.trim().replace(/\s+/g, "");
        if (code) navigate(`/certificado/${encodeURIComponent(code)}`);
      }}
    >
      <label className="label" htmlFor="cert-code">
        Código de validação
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          id="cert-code"
          type="text"
          value={value}
          autoComplete="off"
          placeholder="Ex.: B2C-7F3K-9Q2M-4HXA-…"
          style={{ flex: "1 1 220px" }}
          onChange={(e) => setValue(e.target.value)}
        />
        <button className="btn" disabled={!value.trim()}>
          Validar
        </button>
      </div>
      <p className="segment-meta" style={{ margin: 0 }}>
        O código aparece no rodapé do certificado em PDF.
      </p>
    </form>
  );
}

export function CertificateVerifyPage() {
  const { code = "" } = useParams();
  if (!code)
    return (
      <main className="page narrow">
        <div className="card card-pad verify">
          <div className="eyebrow">Validação de certificado</div>
          <h1 style={{ fontSize: "1.4rem" }}>Confira a autenticidade de um certificado</h1>
          <p style={{ color: "var(--ink-2)" }}>
            Digite o código impresso no certificado para ver o nome do aluno, o curso, a carga horária e a
            data de emissão.
          </p>
          <CertificateCodeForm />
        </div>
      </main>
    );
  return <CertificateResult code={code} />;
}

function CertificateResult({ code }: { code: string }) {
  const [data, setData] = useState<{
    valid: boolean;
    studentName: string;
    courseTitle: string;
    workloadHours: number | null;
    institution: string;
    issuedAt: string;
    studentCode: string | null;
    courseCode: string | null;
    institutionCode: string | null;
    completedOn: string | null;
  } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get<NonNullable<typeof data>>(`/certificates/${encodeURIComponent(code)}`)
      .then(setData)
      .catch(() => setError("Certificado não encontrado. Confira o código informado."));
  }, [code]);
  return (
    <main className="page narrow">
      <div className="card card-pad verify">
        <div className="eyebrow">Validação de certificado</div>
        <h1 style={{ fontSize: "1.1rem", wordBreak: "break-all" }}>{code.toUpperCase()}</h1>
        {error ? (
          <>
            <p className="alert error">{error}</p>
            <CertificateCodeForm initial={code} />
          </>
        ) : !data ? (
          <Spinner />
        ) : (
          <>
            <p className={`alert ${data.valid ? "ok" : "error"}`}>
              {data.valid ? "Certificado válido." : "Este certificado foi revogado."}
            </p>
            <dl className="glossary">
              <div>
                <dt>Aluno(a)</dt>
                <dd>{data.studentName}</dd>
              </div>
              <div>
                <dt>Curso</dt>
                <dd>{data.courseTitle}</dd>
              </div>
              {data.workloadHours && (
                <div>
                  <dt>Carga horária</dt>
                  <dd>{data.workloadHours} hora(s)</dd>
                </div>
              )}
              <div>
                <dt>Instituição</dt>
                <dd>{data.institution}</dd>
              </div>
              <div>
                <dt>Emissão</dt>
                <dd>
                  {new Date(data.issuedAt).toLocaleDateString("pt-BR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </dd>
              </div>
              {data.studentCode && (
                <div>
                  <dt>Códigos</dt>
                  <dd>
                    Aluno <code>{data.studentCode}</code> · Curso <code>{data.courseCode}</code> · Instituição{" "}
                    <code>{data.institutionCode}</code>
                    {data.completedOn
                      ? ` · conclusão em ${data.completedOn.split("-").reverse().join("/")}`
                      : ""}
                  </dd>
                </div>
              )}
            </dl>
          </>
        )}
      </div>
    </main>
  );
}
